/**
 * Whether we should keep showing “loading” while catching the user up to the
 * page they were on (e.g. Back to All Activity page 5).
 *
 * Real-world case: you hit Back to page 5, but the very first page of results
 * fails to load (network blip, server error). We used to see “zero pages so
 * far” and keep spinning forever, as if we were still on the way to page 5.
 * Once that first request has failed — or we are not fetching and cannot go
 * further — we stop waiting so the error / empty UI can show instead of a
 * stuck spinner.
 */
export const isWaitingForRestoredPage = (options: {
	currentPage: number;
	hasNextPage?: boolean;
	isError?: boolean;
	isFetching: boolean;
	loadedPages: number;
}): boolean => {
	if (options.currentPage <= options.loadedPages) {
		return false;
	}

	// No pages in memory yet (common right after Back, before page 1 lands).
	if (options.loadedPages === 0) {
		if (options.currentPage <= 1) {
			return false;
		}
		// First page failed — we cannot walk to the saved page. Stop waiting and show the normal UI error state.
		if (options.isError) {
			return false;
		}
		// Still requesting the first page — keep the restore spinner up.
		if (options.isFetching) {
			return true;
		}
		// Infinite query often reports `hasNextPage: false` before any page
		// exists. With no error and a target past page 1, treat that as
		// “restore still starting,” not “done.”
		return true;
	}

	// Pages are already in memory. `isError` is deliberately not consulted here,
	// even though a later page can fail mid-walk.
	//
	// That failure already stops the walk directly: `ensurePageLoaded` throws
	// when a fetch comes back without growing the cache, and the walk driver's
	// catch gives up in the same tick — clearing the spinner and clamping the
	// page together. Answering "not waiting" here instead would clear the
	// spinner on the render where the error appears, which is before the
	// give-up runs, leaving the user looking at an empty page past the end of
	// the data until the clamp catches up.
	return options.isFetching || options.hasNextPage !== false;
};
